import { FC } from 'react';

import { UseFormRegister, Control, Controller, FieldErrors } from 'react-hook-form';

import RichText from '@components/RichText/RichText';
import { wrap } from '@sitecore/common';
import type { DeceasedFormRendering } from '@sitecore/types/DeceasedForm';
import { Heading, InputEmail, InputText, InputTelephone, RadioGroup, RadioButton, Grid, Text } from '@sparky';

import type { FormValues } from '../types';

type Props = {
  register: UseFormRegister<FormValues>;
  fields: DeceasedFormRendering['fields']['contactFormFields'];
  control: Control<FormValues, string>;
  errors: FieldErrors<FormValues>;
};

export const ContactInputs: FC<Props> = ({ register, fields, errors, control }) => {
  const {
    title,
    description,
    salutationFormField,
    firstNameFormField,
    surnamePrepositionFormField,
    surnameFormField,
    emailAddressFormField,
    houseNumberFormField,
    houseNumberSuffixFormField,
    phoneNumberFormField,
    postalCodeFormField,
  } = fields;
  return (
    <>
      <Heading as="h3" size="S">
        {wrap(title)}
      </Heading>
      <Text size={{ initial: 'BodyM', md: 'BodyL' }}>{wrap(description, <RichText html={description.value} />)}</Text>
      <Controller
        control={control}
        name="contactSalutation"
        render={({ field: { onChange, value, name } }) => (
          <RadioGroup
            name={name}
            value={value}
            onValueChange={onChange}
            label={salutationFormField.value.label}
            error={errors?.contactSalutation?.message}
            hint={salutationFormField.value.hint}>
            <RadioButton value="dhr">
              {salutationFormField.value.options.find(option => option.name === 'mr')?.label ?? `Dhr.`}
            </RadioButton>
            <RadioButton value="mevr">
              {salutationFormField.value.options.find(option => option.name === 'mrs')?.label ?? `Mevr.`}
            </RadioButton>
          </RadioGroup>
        )}
      />

      <Grid columns={{ initial: '1', md: '2', lg: '3' }} gap="6">
        <InputText
          label={firstNameFormField.value.label}
          error={errors?.contactFirstName?.message}
          hint={firstNameFormField.value.hint}
          placeholder={firstNameFormField.value.placeholder}
          {...register('contactFirstName')}
        />
        <InputText
          label={surnamePrepositionFormField.value.label}
          error={errors?.contactSurnamePreposition?.message}
          hint={surnameFormField.value.hint}
          placeholder={surnameFormField.value.placeholder}
          {...register('contactSurnamePreposition')}
          isOptional
        />
        <InputText
          label={surnameFormField.value.label}
          error={errors?.contactSurname?.message}
          hint={surnameFormField.value.hint}
          placeholder={surnameFormField.value.placeholder}
          {...register('contactSurname')}
        />
      </Grid>
      <Grid columns={{ initial: '1', md: '3' }} gap="6">
        <InputText
          label={postalCodeFormField.value.label}
          error={errors?.contactPostalCode?.message}
          hint={postalCodeFormField.value.hint}
          placeholder={postalCodeFormField.value.placeholder}
          {...register('contactPostalCode')}
        />
        <InputText
          label={houseNumberFormField.value.label}
          error={errors?.contactHouseNumber?.message}
          hint={houseNumberFormField.value.hint}
          placeholder={houseNumberFormField.value.placeholder}
          {...register('contactHouseNumber')}
        />
        <InputText
          label={houseNumberSuffixFormField.value.label}
          error={errors?.contactHouseNumberSuffix?.message}
          hint={houseNumberSuffixFormField.value.hint}
          placeholder={houseNumberFormField.value.placeholder}
          isOptional
          {...register('contactHouseNumberSuffix')}
        />
      </Grid>
      <Grid columns={{ initial: '1', md: '2' }} gap="6">
        <InputTelephone
          label={phoneNumberFormField.value.label}
          autoComplete="tel"
          error={errors?.contactPhoneNumber?.message}
          hint={phoneNumberFormField.value.hint}
          placeholder={phoneNumberFormField.value.placeholder}
          {...register('contactPhoneNumber')}
        />
        <InputEmail
          label={emailAddressFormField.value.label}
          autoComplete="email"
          error={errors?.contactEmail?.message}
          hint={emailAddressFormField.value.hint}
          placeholder={emailAddressFormField.value.placeholder}
          {...register('contactEmail')}
        />
      </Grid>
    </>
  );
};
